/**
 * Which of the two front doors this visit is at.
 *
 * `/` is the **landing page** — the pitch, the terminal, the chapters — for
 * everybody who has not named a screen inside the course. The **ledger**, a
 * learner's own record drawn as a commit history, is `/?view=course`, and it
 * is one click from the top of the landing page. This module is the whole of
 * the decision, read once in main.tsx before React mounts.
 *
 * ## It did not always work this way, and why it does now
 *
 * The first version of this split branched on whether the browser had a
 * course record: a stranger got the pitch, a returning learner went straight
 * to their ledger. The argument for it was real — showing a sales page to
 * somebody mid-course is friction they did not ask for — but it bought that
 * at a price nobody had named, which is that the front page of the product
 * became a page that nobody who works on it, and no learner who had ever
 * opened a single level, would see again. A front door that hides itself from
 * everyone who has been through it once is a door nobody maintains, and the
 * first person to notice it had gone was the person who owns it.
 *
 * So `/` is the pitch now, for everybody, and the returning learner is paid
 * back where the cost actually lands rather than by being routed around the
 * page: the landing page reads their record and its one call to action
 * becomes *their* next level instead of level 0, under a band that goes
 * straight to their ledger (components/home/Landing.tsx). What a learner
 * mid-course loses is a page they can scroll; what they keep is one click, at
 * the top, to a level rather than to a list.
 *
 * ## Why the answer still has to be synchronous
 *
 * Because of what the wrong answer costs. `App` boots a virtual machine on
 * mount and renders nothing until it is up (App.tsx: everything is gated on
 * `missions`, and `missions` is set by `Promise.all([listMissions,
 * bootRuntime])`). So a visit that should have been the landing page and is
 * not is a reader charged tens of megabytes to read a first sentence, on the
 * one screen in the product where that costs the most.
 *
 * The landing page therefore mounts *instead of* `App`, the way the public
 * profile and the agent page already do, and for the argument written at
 * the top of both: somebody who has not asked for a course should not be made
 * to download one. That only works if the choice is made before anything
 * mounts, which rules out asking the server anything — and it is why the
 * record below is read out of local storage rather than from the account.
 */
import type { Progress } from '@engineer-os/engine';

/** The landing page's own address, for the ledger's footer link and for
 *  anything that wants to link the pitch rather than the course. */
export const ABOUT_PATH = '/about';

/**
 * The query keys `App` answers to.
 *
 * A link that names one of these is a link into the course — a level, a
 * record, an article, the board's own escape hatch — so it mounts `App`
 * whoever is asking for it. That is what makes the landing page's own call to
 * action work: it points at `?mission=<id>`, and a level is not the pitch.
 * `work` opened a paid task until those retired on 2026-10-31; it stays so
 * that an old link lands on the course rather than on the pitch.
 */
const APP_KEYS = ['mission', 'level', 'profile', 'debrief', 'opener', 'sandbox', 'work'] as const;

/**
 * The one way to ask for the course without naming a screen inside it.
 *
 * This is the ledger's address. Since `/` stopped being it, every "home" link
 * inside the app points here instead (`COURSE_HREF`), which is what keeps the
 * ledger a page somebody can reload, bookmark and send — the thing it quietly
 * stopped being the moment `/` became the pitch for everybody.
 *
 * Also the landing page's own failure path: a catalogue it cannot read is one
 * `App`'s boot screen already knows how to explain, in both languages, so the
 * visit is handed over rather than drawn as a pitch with no numbers in it.
 */
export const COURSE_QUERY = 'view=course';

/** The same thing as an `href`, because every link in the product is a real
 *  `<a href="?…">` that App's one delegated listener turns into a `pushState`
 *  (App.tsx, `useRoute`). `readRoute` does not read `view`, so this parses as
 *  no screen at all, which is the ledger. */
export const COURSE_HREF = `?${COURSE_QUERY}`;

/** Is this the pitch, spelled out? */
export function isAboutPath(pathname: string): boolean {
  return pathname === ABOUT_PATH || pathname === `${ABOUT_PATH}/`;
}

/**
 * Has this browser been used for the course?
 *
 * Any XP, or any level with an entry against it. An *attempted* level counts,
 * not only a cleared one: somebody who opened level 0 and walked away has a
 * place to be returned to, and the landing page's whole accommodation for a
 * returning learner is built on being able to name it.
 *
 * Pure, and given the record rather than reading it, because the caller needs
 * the record anyway — the landing page loads it once and spends it on this
 * and on the frontier both (components/home/Landing.tsx).
 */
export function hasRecord(progress: Progress): boolean {
  return progress.xp > 0 || Object.keys(progress.levels).length > 0;
}

/**
 * Should this visit be the landing page rather than the app?
 *
 * The clock of the decision, in order: the pitch's own address always wins,
 * anything that is not the root is somebody else's route, an explicit ask for
 * the course is the course, a link that names a screen inside the course is
 * the course — and what is left, which is a bare `/`, is the pitch.
 *
 * It no longer asks anything about the browser. That is the change: the last
 * line used to be `return !hasRecord`.
 */
export function wantsLanding(pathname: string, search: string): boolean {
  if (isAboutPath(pathname)) return true;
  if (pathname !== '/' && pathname !== '') return false;
  const params = new URLSearchParams(search);
  if (params.get('view') === 'course') return false;
  if (APP_KEYS.some((key) => params.has(key))) return false;
  return true;
}
